import posthog from 'posthog-js'
import { supabase } from '@/integrations/supabase/client'

const POSTHOG_KEY_CACHE = 'socrates-posthog-key'
const POSTHOG_HOST_CACHE = 'socrates-posthog-host'
const POSTHOG_CACHE_EXPIRY = 'socrates-posthog-expiry'
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000 // 24 hours

function getCachedConfig(): { key: string; host: string } | null {
  const key = localStorage.getItem(POSTHOG_KEY_CACHE)
  const host = localStorage.getItem(POSTHOG_HOST_CACHE)
  const expiry = localStorage.getItem(POSTHOG_CACHE_EXPIRY)

  if (!key || !expiry) return null
  if (Date.now() > parseInt(expiry, 10)) {
    localStorage.removeItem(POSTHOG_KEY_CACHE)
    localStorage.removeItem(POSTHOG_HOST_CACHE)
    localStorage.removeItem(POSTHOG_CACHE_EXPIRY)
    return null
  }

  return { key, host: host || 'https://us.i.posthog.com' }
}

function setCachedConfig(key: string, host: string): void {
  localStorage.setItem(POSTHOG_KEY_CACHE, key)
  localStorage.setItem(POSTHOG_HOST_CACHE, host)
  localStorage.setItem(POSTHOG_CACHE_EXPIRY, (Date.now() + CACHE_DURATION_MS).toString())
}

/**
 * Initialize PostHog with config from Supabase secrets (via get-app-config).
 * Caches in localStorage to avoid fetching on every page load.
 */
export async function initPostHog() {
  let config = getCachedConfig()

  if (!config) {
    try {
      const { data, error } = await supabase.functions.invoke('get-app-config')
      if (!error && data?.posthogKey) {
        config = { key: data.posthogKey, host: data.posthogHost || 'https://us.i.posthog.com' }
        setCachedConfig(config.key, config.host)
      }
    } catch (err) {
      console.warn('Failed to fetch PostHog config:', err)
      // Try expired cache as fallback
      const expiredKey = localStorage.getItem(POSTHOG_KEY_CACHE)
      if (expiredKey) {
        config = { key: expiredKey, host: localStorage.getItem(POSTHOG_HOST_CACHE) || 'https://us.i.posthog.com' }
      }
    }
  }

  if (!config) return

  posthog.init(config.key, {
    api_host: config.host,
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: true,
  })
}

export { posthog }
